import React, { useContext, useState } from 'react';
import {
  Box, Button, Checkbox, Divider, FormControlLabel, FormGroup, IconButton, TextField, Typography,
} from '@mui/material';
import { FormattedMessage } from 'react-intl';
import CloseIcon from '@mui/icons-material/Close';
import {
  GroundTruthString, GTStringPayload, PartialBy, QuestionString, QuestionTypeV2,
} from '../../redux/types';
import { } from '@mui/icons-material';
import ColorContext from '../../context/ColorContext';
import GTResetButton from './GTResetButton';

type Props = {
  surveyId: number;
  sectionId: number;
  questionId: number;
  caseId: number;
  question: QuestionString;
  groundTruthId?: number;
  payload?: GTStringPayload;
  gtGroupId: number|null;
  onSave: (gtData: PartialBy<GroundTruthString, 'id'>) => void;
  onCancel: () => void;
  onDelete: (dgtId: number) => void;
};

const getInitialStringPayload = (): GTStringPayload => {
  const payload: GTStringPayload = [{
    text: '',
    score: 0,
    caseSensitive: false,
  }];
  return payload;
};

const GTStringScoreEditor: React.FC<Props> = ({
  question,
  payload,
  surveyId,
  sectionId,
  questionId,
  caseId,
  groundTruthId,
  gtGroupId,
  onSave,
  onCancel,
  onDelete,
}: Props) => {
  const color = useContext(ColorContext);
  const [payloadBuffer, setpayloadBuffer] = useState<GTStringPayload>(
    payload || getInitialStringPayload(),
  );

  const saveHandler = () => {
    if (!payloadBuffer || !payloadBuffer.length) {
      return;
    }
    const newGTRecord: PartialBy<GroundTruthString, 'id'> = {
      id: groundTruthId,
      digitalSurveyId: surveyId,
      digitalSectionId: sectionId,
      digitalQuestionId: questionId,
      digitalCaseId: caseId,
      type: QuestionTypeV2.String,
      payload: payloadBuffer.map((p) => ({ ...p, text: p.text.trim() })),
      digitalGroundTruthGroupId: gtGroupId,
    };
    onSave(newGTRecord);
  };

  const handleCaseSensitiveCheck = (event: React.ChangeEvent<HTMLInputElement>, index: number) => {
    const value = event.target.checked;
    const newPayloadBuffer = payloadBuffer.map((pb, i) => {
      if (index === i) {
        return {
          ...pb,
          caseSensitive: value,
        };
      }
      return pb;
    });
    setpayloadBuffer(newPayloadBuffer);
  };

  const handleAnswerChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, index: number,
  ) => {
    const { value } = e.target;
    const newPayloadBuffer = payloadBuffer.map((pb, i) => {
      if (index === i) {
        return {
          ...pb,
          text: value,
        };
      }
      return pb;
    });
    setpayloadBuffer(newPayloadBuffer);
  };

  const handleScoreChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, index: number,
  ) => {
    const value = Number(e.target.value);
    const newPayloadBuffer = payloadBuffer.map((pb, i) => {
      if (index === i) {
        return {
          ...pb,
          score: value,
        };
      }
      return pb;
    });
    setpayloadBuffer(newPayloadBuffer);
  };

  const addEntryHandler = () => {
    setpayloadBuffer(payloadBuffer.concat(getInitialStringPayload()));
  };

  const deleteEntryHandler = (index: number) => {
    const newArr = payloadBuffer.slice();
    newArr.splice(index, 1);
    setpayloadBuffer(newArr);
  };

  return (
    <Box>
      <Box sx={{ mx: 2, my: 3 }}>
        <Typography color="GrayText">{question.text}</Typography>
        {
          payloadBuffer.map((p, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <Box key={index} sx={{ my: 2 }} display="flex" gap={2}>
              <Box flexGrow={0.9}>
                <TextField label="text" value={p.text} onChange={(e) => { handleAnswerChange(e, index); }} size="small" fullWidth />
              </Box>
              <TextField label="Score" value={p.score} onChange={(e) => handleScoreChange(e, index)} size="small" type="number" sx={{ width: 100 }} />
              <FormGroup>
                <FormControlLabel control={<Checkbox size="small" checked={p.caseSensitive} onChange={(e) => handleCaseSensitiveCheck(e, index)} />} label="Case sensitive" />
              </FormGroup>
              <IconButton onClick={() => deleteEntryHandler(index)}>
                <CloseIcon />
              </IconButton>
            </Box>
          ))
        }
        <Box display="flex" justifyContent="flex-end" sx={{ my: 1 }}>
          <Button onClick={addEntryHandler}>Add Entry</Button>
        </Box>
      </Box>
      <Divider />
      <Box display="flex" justifyContent="flex-end" sx={{ mt: 1 }} gap={1}>
        <Box flexGrow={1}>
          <GTResetButton groundTruthId={groundTruthId} onReset={onDelete} />
        </Box>
        <Button variant="contained" color={color} onClick={saveHandler}><FormattedMessage id="save" /></Button>
        <Button variant="contained" color="secondary" onClick={onCancel}><FormattedMessage id="genericCancel" /></Button>
      </Box>
    </Box>
  );
};

export default GTStringScoreEditor;
